/**
 * SaveSuccessModal - 保存配置成功后的弹窗
 * 显示二维码、配置详情链接，以及提交官方审核选项
 */

import { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useLanguage } from '../../context/LanguageContext';
import * as api from '../../services/api';
import styles from './SaveSuccessModal.module.css';

export default function SaveSuccessModal({
    isOpen,
    onClose,
    configId,
    configTitle,
    totalPrice,
    onSubmitForReview
}) {
    const { t } = useLanguage();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [editableTitle, setEditableTitle] = useState(configTitle || '');
    const [isSavingTitle, setIsSavingTitle] = useState(false);

    // 当 configTitle 变化时更新 editableTitle
    useEffect(() => {
        setEditableTitle(configTitle || '');
    }, [configTitle]);

    // 配置详情页面 URL
    const configUrl = `${window.location.origin}/config/${configId}`;

    // 复制链接到剪贴板
    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(configUrl);
            alert('链接已复制！ / Link copied!');
        } catch (err) {
            console.error('Copy failed:', err);
            // 备选方案
            const input = document.createElement('input');
            input.value = configUrl;
            document.body.appendChild(input);
            input.select();
            document.execCommand('copy');
            document.body.removeChild(input);
            alert('链接已复制！ / Link copied!');
        }
    };

    // 提交官方审核
    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            await onSubmitForReview();
            setIsSubmitted(true);
        } catch (error) {
            console.error('Submit error:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    // 保存标题并关闭
    const handleSaveAndClose = async () => {
        // 如果标题有修改，先保存
        if (editableTitle.trim() && editableTitle !== configTitle && configId) {
            setIsSavingTitle(true);
            try {
                await api.updateConfiguration(configId, { title: editableTitle.trim() });
                console.log('✅ Title updated successfully');
            } catch (error) {
                console.error('Failed to update title:', error);
            } finally {
                setIsSavingTitle(false);
            }
        }
        onClose();
    };

    // 重置状态
    useEffect(() => {
        if (!isOpen) {
            setIsSubmitted(false);
            setIsSubmitting(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className={styles.overlay} onClick={handleSaveAndClose}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>
                {/* 成功图标和标题 */}
                <div className={styles.header}>
                    <div className={styles.successIcon}>✓</div>
                    <h2 className={styles.title}>配置已保存</h2>
                    <p className={styles.subtitle}>Configuration Saved Successfully</p>
                </div>

                {/* 配置信息 */}
                <div className={styles.configInfo}>
                    <p className={styles.configLabel}>{t('saveSuccess.configName')}</p>
                    <input
                        type="text"
                        className={styles.configTitleInput}
                        value={editableTitle}
                        onChange={(e) => setEditableTitle(e.target.value)}
                        placeholder="输入配置名称 / Enter config name"
                    />
                    <p className={styles.configPrice}>${totalPrice?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                </div>

                {/* 二维码区域 */}
                <div className={styles.qrSection}>
                    <p className={styles.qrLabel}>扫码查看配置详情 / Scan to view details</p>
                    <div className={styles.qrCode}>
                        <QRCodeSVG
                            value={configUrl}
                            size={160}
                            level="H"
                            includeMargin={true}
                            bgColor="#ffffff"
                            fgColor="#000000"
                        />
                    </div>
                    <button className={styles.copyBtn} onClick={handleCopyLink}>
                        📋 复制链接 / Copy Link
                    </button>
                </div>

                {/* 分隔线 */}
                <div className={styles.divider}>
                    <span>更进一步 / Go Further</span>
                </div>

                {/* 提交官方审核区域 */}
                <div className={styles.submitSection}>
                    {!isSubmitted ? (
                        <>
                            <h4 className={styles.submitTitle}>🏆 提交到官方精选配置</h4>
                            <p className={styles.submitDesc}>
                                让您的创意配置有机会被哈雷官方收录，展示给全球车友！
                            </p>
                            <p className={styles.submitDescEn}>
                                Submit your creative build to be featured in Harley-Davidson's official gallery!
                            </p>
                            <button
                                className={styles.submitBtn}
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? '提交中...' : '🚀 提交官方审核'}
                            </button>
                        </>
                    ) : (
                        <div className={styles.submittedMessage}>
                            <div className={styles.submittedIcon}>🎉</div>
                            <h4>已提交审核</h4>
                            <p>感谢您的分享！我们将尽快审核您的配置。</p>
                            <p className={styles.submittedEn}>Thank you! We'll review your configuration soon.</p>
                        </div>
                    )}
                </div>

                {/* 关闭按钮 - 保存名称并关闭 */}
                <button
                    className={styles.closeBtn}
                    onClick={handleSaveAndClose}
                    disabled={isSavingTitle}
                >
                    {isSavingTitle ? '保存中... / Saving...' : '完成 / Done'}
                </button>
            </div>
        </div>
    );
}
